import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";

/* Intake Read — Screens A (submit) / B (loading) / C (result) / E (error).
 *
 * Spec: Emergent Build Brief — The Gauntlet: Intake Read v1.0.
 * All locked copy from §11 Decision 2 is verbatim.
 *
 * The number is called "readiness to be evaluated", NEVER "score" on this
 * screen or on Screen A. §11 forbids: the word "passing", the number 85,
 * and any reference to advancement anywhere on the intake screen.
 */

const BAND_TEST_ID = {
  tuneup: "band-tuneup",
  soft_spots: "band-soft-spots",
  real_work: "band-real-work",
  long_haul: "band-long-haul",
};

const DELIVERABLES = [
  { name: "Simple brief",
    desc: "The decision on one page. What it is, who buys, what it costs." },
  { name: "Full-depth brief",
    desc: "Every mechanic, every tradeoff, every rejected option. Hand to a developer." },
  { name: "Checklist scorecard",
    desc: "Every criterion, scored, with evidence. Open items named, not hidden." },
  { name: "Investor one-pager",
    desc: "Trajectory, thresholds, multiple. Shark Panel sweep attached." },
  { name: "Dashboard seed",
    desc: "Ring 4 milestones written into the ledger as plan-vs-actual rows." },
  { name: "Go / no-go verdict",
    desc: "A number, a reason, and the one question to answer before spending a dollar." },
];

function SubmitScreen({ initialName = "", onSubmit, loading, error }) {
  const [ideaName, setIdeaName] = useState(initialName);
  const [file, setFile] = useState(null);
  const [paste, setPaste] = useState("");
  const fileRef = useRef(null);

  const nameOk = ideaName.trim().length > 0 && ideaName.trim().length <= 80;
  const inputOk = !!file || paste.trim().length >= 200;
  const canSubmit = nameOk && inputOk && !loading;

  return (
    <div className="max-w-2xl mx-auto px-8 py-14">
      <div className="mono-caps mb-3 text-slate-500">Intake read</div>
      <h1 className="font-display text-4xl sm:text-5xl mb-4" style={{ color: "#16293b" }}>
        Submit your idea.
      </h1>
      <p className="text-slate-600 mb-10 leading-relaxed" data-testid="intake-explainer">
        This measures whether your idea is ready to be evaluated — not whether
        it&apos;s a good idea. It has no effect on your Gauntlet score.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit({ ideaName: ideaName.trim(), file, paste: paste.trim() });
        }}
        className="space-y-8"
      >
        <div>
          <label className="mono-caps block mb-3">Idea name</label>
          <input
            value={ideaName}
            onChange={(e) => setIdeaName(e.target.value)}
            placeholder="A one-line label for this submission"
            className="input-line"
            maxLength={80}
            data-testid="intake-idea-name"
          />
          <div className="mt-1 mono-caps text-slate-400 text-[0.6rem]">
            {ideaName.length}/80
          </div>
        </div>

        <div>
          <label className="mono-caps block mb-3">Upload document</label>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.md,.txt"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
            data-testid="intake-file-input"
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="btn-ghost"
              data-testid="intake-file-button"
            >
              {file ? "Change file" : "Choose file"}
            </button>
            {file && (
              <>
                <span className="mono-caps text-slate-600 text-[0.7rem]" data-testid="intake-file-name">
                  {file.name} · {(file.size / 1024).toFixed(0)} KB
                </span>
                <button
                  type="button"
                  onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="text-slate-500 hover:text-slate-700 text-xs underline"
                  data-testid="intake-file-clear"
                >
                  clear
                </button>
              </>
            )}
          </div>
          <div className="mt-2 mono-caps text-slate-400 text-[0.6rem]">
            Accepted: .pdf, .docx, .md, .txt · max 10 MB
          </div>
        </div>

        <div className="text-center mono-caps text-slate-400 text-[0.7rem]">— or —</div>

        <div>
          <label className="mono-caps block mb-3">Paste the text</label>
          <textarea
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            rows={12}
            className="input-line"
            placeholder="Paste at least 200 characters describing the idea."
            data-testid="intake-paste-input"
          />
          <div className="mt-1 mono-caps text-slate-400 text-[0.6rem]">
            {paste.length} characters · minimum 200
          </div>
        </div>

        {file && paste.trim().length >= 200 && (
          <div className="text-slate-500 text-sm" data-testid="intake-both-warning">
            Both a file and paste were provided. The file will be used; the
            pasted text will be ignored.
          </div>
        )}

        {error && (
          <div className="text-red-700 text-sm font-medium" data-testid="intake-error">
            {error}
          </div>
        )}

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={!canSubmit}
            className="btn-primary"
            data-testid="intake-submit"
          >
            {loading ? "Reading the intake…" : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
}

function LoadingScreen({ ideaName, onCancel }) {
  /* Screen B — neutral spinner, no fake progress percentage. §5. */
  return (
    <div className="max-w-xl mx-auto px-8 py-24 text-center">
      <div className="mono-caps text-slate-500 mb-3">Intake read</div>
      <h2 className="font-display text-3xl mb-8" style={{ color: "#16293b" }}>
        {ideaName}
      </h2>
      <div className="flex justify-center mb-6" data-testid="intake-loading-spinner">
        <div
          className="w-8 h-8 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin"
        />
      </div>
      <div className="text-slate-600 mb-8">Reading the intake.</div>
      <button
        onClick={onCancel}
        className="btn-ghost"
        data-testid="intake-loading-cancel"
      >
        Cancel
      </button>
    </div>
  );
}

function ErrorScreen({ message, onRetry }) {
  /* Screen E — one sentence + retry. No stack traces. §5. */
  return (
    <div className="max-w-xl mx-auto px-8 py-24 text-center">
      <div className="mono-caps text-slate-500 mb-3">Intake read</div>
      <h2 className="font-display text-3xl mb-6" style={{ color: "#16293b" }}>
        Scoring couldn&apos;t finish.
      </h2>
      <p className="text-slate-600 mb-8" data-testid="intake-error-message">{message}</p>
      <button onClick={onRetry} className="btn-primary" data-testid="intake-error-retry">
        Try again
      </button>
    </div>
  );
}

function ReadDisplay({ read, onStartRing1, ring1Starting, onBack, showBackButton = true }) {
  /* Screen C — the four vertical blocks per §5. Locked copy throughout. */
  const dims = read.dimensions;
  const dimOrder = Object.keys(dims); // preserves insertion order from backend
  const lowestKey = dimOrder.reduce((lo, k) =>
    read.scores[k].score < read.scores[lo].score ? k : lo, dimOrder[0]);

  return (
    <div className="max-w-3xl mx-auto px-8 py-14">
      {showBackButton && (
        <button
          className="mono-caps hover:text-slate-900 transition-colors mb-8"
          onClick={onBack}
          data-testid="intake-read-back"
        >
          ← Submit another
        </button>
      )}

      {/* Block 1 — Header card */}
      <div
        className="border border-slate-200 bg-white p-8 mb-6"
        data-testid="intake-header-card"
      >
        <div className="text-[13px] text-slate-500 mb-3" data-testid="intake-idea-name-shown">
          {read.idea_name}
        </div>
        <div className="flex items-baseline justify-between flex-wrap gap-4 mb-3">
          <div>
            <span
              className="font-display"
              style={{ fontSize: "44px", color: "#16293b", lineHeight: 1 }}
              data-testid="intake-total-read"
            >
              {read.total_read}
            </span>
            <span className="ml-2 text-[13px] text-slate-500">
              / 100 readiness to be evaluated
            </span>
          </div>
          <span
            className="inline-block px-3 py-1.5 mono-caps text-[13px] font-medium"
            style={{ background: "#f5822a", color: "#FFFFFF" }}
            data-testid={BAND_TEST_ID[read.band.key] || "band-badge"}
          >
            {read.band.label}
          </span>
        </div>
        <div className="text-[13px] text-slate-500 mb-4" data-testid="intake-disclaimer">
          Readiness to be evaluated. This is not your Gauntlet score and does not affect it.
        </div>
        <div className="text-slate-800 leading-relaxed" style={{ color: "#16293b" }}>
          Estimated to complete: <b className="font-medium">{read.band.estimate}</b>. {read.band.detail}
        </div>
        <div className="text-slate-700 mt-2" data-testid="intake-predicted-bounce">
          {read.predicted_bounce}
        </div>
        {read.zero_capped && (
          <div className="mt-4 text-[13px]" style={{ color: "#7A2A20" }} data-testid="intake-cap-note">
            Capped at 59 — {dims[read.capped_dimension]?.label || read.capped_dimension} scored 0.
          </div>
        )}
      </div>

      {/* Block 2 — six dimension tiles */}
      <div
        className="grid gap-3 mb-6"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}
        data-testid="intake-dimension-tiles"
      >
        {dimOrder.map((key) => {
          const dim = read.scores[key];
          const label = dims[key].label;
          const isDanger = dim.score <= 1;
          return (
            <div
              key={key}
              className="border border-slate-200 p-4"
              style={{ background: "#F5F3EC" }}
              title={dim.reason}
              data-testid={`intake-tile-${key}`}
            >
              <div className="text-[13px] text-slate-500 mb-2">{label}</div>
              <div
                className="font-display"
                style={{ fontSize: "24px", color: isDanger ? "#7A2A20" : "#16293b", lineHeight: 1 }}
                data-testid={`intake-tile-value-${key}`}
              >
                {dim.score}/5
              </div>
              {dim.reason && (
                <div className="mt-2 text-xs text-slate-500 italic leading-snug">
                  {dim.reason}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Block 3 — Where the time will go */}
      <div className="border border-slate-200 bg-white p-6 mb-6" data-testid="intake-gaps">
        <div className="text-[13px] text-slate-500 mb-3">Where the time will go</div>
        {read.gaps && read.gaps.length > 0 ? (
          <div className="space-y-2">
            {read.gaps.map((g, i) => (
              <div key={`gap-${i}-${g.slice(0, 24)}`} className="flex items-start gap-3" data-testid={`intake-gap-${i}`}>
                <span
                  className="mono-caps font-medium"
                  style={{
                    color: (i === 0 && read.zero_capped) ? "#7A2A20" : "#7A4E11",
                    minWidth: "16px",
                  }}
                >!</span>
                <span className="text-[14px] text-slate-800" style={{ color: "#16293b" }}>{g}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-slate-500 italic text-sm">
            No specific gaps surfaced. The intake reads as complete.
          </div>
        )}
      </div>

      {/* Block 4 — What you walk out with */}
      <div className="border border-slate-200 bg-white p-6 mb-8" data-testid="intake-deliverables">
        <div className="text-[13px] text-slate-500 mb-1">What you walk out with</div>
        <div className="text-xs text-slate-400 mb-4">
          Generated after Ring 4 clears the Shark Panel
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          {DELIVERABLES.map((d) => (
            <div key={d.name} data-testid={`intake-deliverable-${d.name.toLowerCase().replace(/[\s/]+/g, "-")}`}>
              <div className="text-[14px] font-medium" style={{ color: "#16293b" }}>
                {d.name}
              </div>
              <div className="text-[13px] text-slate-500 leading-snug mt-0.5">
                {d.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={onStartRing1}
          disabled={ring1Starting}
          className="btn-primary"
          data-testid="intake-start-ring1"
        >
          {ring1Starting ? "Opening Ring 1…" : "Start Ring 1"}
        </button>
        <a
          href={`${API}/intake-read/${read.id}/pdf`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost"
          data-testid="intake-export-pdf"
        >
          Export PDF
        </a>
        {showBackButton && (
          <button
            onClick={onBack}
            className="btn-ghost"
            data-testid="intake-back-to-submit"
          >
            Back to submit
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Container ────────────────────────────────────────────────────────
 * When mounted at `/intake-read` — Screen A → B → C.
 * When mounted at `/intake-read/:id` — loads existing read, shows Screen C.
 */
export default function IntakeRead() {
  const { id: routeId } = useParams();
  const nav = useNavigate();
  const [screen, setScreen] = useState(routeId ? "loading" : "submit");
  const [read, setRead] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ring1Starting, setRing1Starting] = useState(false);
  const [ideaName, setIdeaName] = useState("");

  // Load existing read when route contains an id
  useEffect(() => {
    if (!routeId) return;
    (async () => {
      try {
        const r = await api.get(`/intake-read/${routeId}`);
        setRead(r.data.intake_read);
        setScreen("result");
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load that read.");
        setScreen("error");
      }
    })();
  }, [routeId]);

  async function handleSubmit({ ideaName, file, paste }) {
    setSubmitting(true);
    setIdeaName(ideaName);
    setError(null);
    setScreen("loading");
    try {
      const form = new FormData();
      form.append("idea_name", ideaName);
      if (file) form.append("file", file);
      else form.append("paste", paste);
      const resp = await fetch(`${API}/intake-read`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || "Could not read that submission.");
      }
      const data = await resp.json();
      setRead(data.intake_read);
      setScreen("result");
    } catch (e) {
      setError(e.message || "Scoring failed.");
      setScreen("error");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStartRing1() {
    if (!read) return;
    setRing1Starting(true);
    try {
      const r = await api.post(`/intake-read/${read.id}/start-ring1`);
      nav(`/ideas/${r.data.idea_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not open Ring 1.");
      setRing1Starting(false);
    }
  }

  if (screen === "submit") {
    return (
      <div className="min-h-screen">
        <div className="max-w-3xl mx-auto pt-6 px-8">
          <button
            className="mono-caps text-slate-500 hover:text-slate-800"
            onClick={() => nav("/dashboard")}
            data-testid="intake-back-to-dashboard"
          >
            ← Dashboard
          </button>
        </div>
        <SubmitScreen
          initialName={ideaName}
          onSubmit={handleSubmit}
          loading={submitting}
          error={error}
        />
      </div>
    );
  }
  if (screen === "loading") {
    return (
      <div className="min-h-screen">
        <LoadingScreen ideaName={ideaName || (read?.idea_name)} onCancel={() => setScreen("submit")} />
      </div>
    );
  }
  if (screen === "error") {
    return (
      <div className="min-h-screen">
        <ErrorScreen message={error || "Something went wrong."} onRetry={() => setScreen("submit")} />
      </div>
    );
  }
  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto pt-6 px-8">
        <button
          className="mono-caps text-slate-500 hover:text-slate-800"
          onClick={() => nav("/dashboard")}
          data-testid="intake-back-to-dashboard"
        >
          ← Dashboard
        </button>
      </div>
      <ReadDisplay
        read={read}
        onStartRing1={handleStartRing1}
        ring1Starting={ring1Starting}
        onBack={() => nav("/intake-read")}
        showBackButton={!routeId}
      />
    </div>
  );
}
